import React from 'react';
import PropTypes from 'prop-types';
import { gql } from '@common/hooks';
import moment from 'moment';
import { Tooltip } from 'antd';
import { ValueWithOriginal } from '@clinician-profile/components';
import { DateWithoutTimeZone, HeaderBar } from '@common/components';
import { Colors, FontSize } from '@common/tokens';

const StrongColumnLabel = ({ children }) => {
  return (
    <strong className="strong-column-label">
      {children}
      <style jsx>{`
        .strong-column-label {
          display: block;
          color: ${Colors.midnightDarkest};
          font-weight: 800;
          font-size: 13px;
        }
      `}</style>
    </strong>
  );
};

export function StaffingRequestAssignmentPageHeader({
  staffingRequestAssignment,
  children,
}) {
  const state = staffingRequestAssignment.state;
  const staffingRequest = staffingRequestAssignment.staffingRequest;

  return (
    <HeaderBar>
      <HeaderBar.Column xs={10} md={6} lg={3}>
        <strong className="assignment-link">Assignment</strong>
      </HeaderBar.Column>
      <HeaderBar.Column
        xs={12}
        md={6}
        lg={3}
        label={
          <StrongColumnLabel>
            {staffingRequest.organization.name}
          </StrongColumnLabel>
        }
        divider={true}
      >
        {staffingRequest.organizationUnit.department.organizationBusiness.city}
        ,&nbsp;
        {
          staffingRequest.organizationUnit.department.organizationBusiness
            .stateCode
        }
      </HeaderBar.Column>
      <HeaderBar.Column
        xs={12}
        md={6}
        lg={3}
        label={
          <StrongColumnLabel>
            {staffingRequest.organizationUnit.clinicalUnit.displayName}
          </StrongColumnLabel>
        }
      >
        {staffingRequest.organizationUnit.department.organizationBusiness.name}
      </HeaderBar.Column>
      <HeaderBar.Column xs={0} md={3} lg={1} label="Role" divider>
        {staffingRequest.role.abbreviation}
      </HeaderBar.Column>
      <HeaderBar.Column xs={0} md={3} lg={1} label="Type" divider>
        <span className="capitalize">
          <ValueWithOriginal
            label="Type"
            value={staffingRequestAssignment.shiftType}
            original={staffingRequestAssignment.originalShiftType}
          />
        </span>
      </HeaderBar.Column>
      <HeaderBar.Column xs={0} md={3} lg={1} label="Shift" divider>
        <ValueWithOriginal
          label="Shift"
          value={`${staffingRequestAssignment.shiftsPerWeek} x ${staffingRequestAssignment.hoursPerShift}`}
          original={`${staffingRequestAssignment.originalShiftsPerWeek} x ${staffingRequestAssignment.originalHoursPerShift}`}
        />
      </HeaderBar.Column>
      <HeaderBar.Column xs={0} lg={2} label="Start Date" divider>
        <DateWithoutTimeZone date={staffingRequestAssignment.startDate} />
      </HeaderBar.Column>
      <HeaderBar.Column xs={0} lg={2} label="End Date" divider>
        <ValueWithOriginal
          label="End Date"
          value={
            staffingRequestAssignment.finalEndDate && (
              <DateWithoutTimeZone
                date={staffingRequestAssignment.finalEndDate}
              />
            )
          }
          original={
            staffingRequestAssignment.originalEndDate &&
            moment(staffingRequestAssignment.originalEndDate).format(
              'MM/DD/YYYY'
            )
          }
        />
      </HeaderBar.Column>
      <HeaderBar.Column xs={0} md={3} lg={2} label="Length" divider>
        <ValueWithOriginal
          label="Length"
          value={staffingRequestAssignment.totalLengthInWeeks}
          original={staffingRequestAssignment.totalLengthInWeeks}
          suffix="weeks"
        />
      </HeaderBar.Column>
      <HeaderBar.Column xs={0} md={3} lg={2} label="Rate" divider>
        <ValueWithOriginal
          label="Rate"
          value={new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
          }).format(staffingRequestAssignment.billingRegularRate)}
          original={new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
          }).format(staffingRequestAssignment.originalBillingRegularRate)}
          suffix=""
        />
      </HeaderBar.Column>

      {state !== 'onboarding' && (
        <HeaderBar.Column xs={0} lg={2} label="Time Off" divider>
          <Tooltip title={staffingRequestAssignment.timeoffDescription || '--'}>
            <span>{staffingRequestAssignment.timeoffDescription || '--'}</span>
          </Tooltip>
        </HeaderBar.Column>
      )}

      {children && (
        <HeaderBar.Column xs={1} md={1} lg={1} label="" divider>
          {children}
        </HeaderBar.Column>
      )}
      <style jsx>{`
        .assignment-link {
          line-height: 50px;
          font-weight: 800;
          font-size: ${FontSize.md};
          color: #1d8292;
        }
      `}</style>
    </HeaderBar>
  );
}

StaffingRequestAssignmentPageHeader.fragments = {
  headerInformation: gql`
    fragment StaffingRequestAssignmentPageHeaderInfo on StaffingRequestAssignment {
      id
      state
      startDate
      endDate
      originalEndDate
      finalEndDate
      timeoffDescription
      originalTimeoffDescription
      billingRegularRate
      originalBillingRegularRate
      shiftsPerWeek
      hoursPerShift
      originalShiftsPerWeek
      originalHoursPerShift
      shiftType
      originalShiftType
      lengthInWeeks
      totalLengthInWeeks

      staffingRequest {
        id
        length

        organization {
          id
          name
        }

        organizationUnit {
          id
          name

          clinicalUnit {
            id
            displayName
          }

          department {
            id
            name

            organizationBusiness {
              id
              name
              city
              stateCode
            }
          }
        }

        role {
          id
          abbreviation
        }
      }
    }
  `,
};

StaffingRequestAssignmentPageHeader.propTypes = {
  staffingRequestAssignment: PropTypes.shape({
    id: PropTypes.string.isRequired,
    state: PropTypes.string.isRequired,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    finalEndDate: PropTypes.string,
    originalEndDate: PropTypes.string,
    timeoffDescription: PropTypes.string,
    originalTimeoffDescription: PropTypes.string,
    billingRegularRate: PropTypes.number,
    originalBillingRegularRate: PropTypes.number,
    originalShiftsPerWeek: PropTypes.number,
    originalHoursPerShift: PropTypes.number,
    shiftsPerWeek: PropTypes.number,
    hoursPerShift: PropTypes.number,
    shiftType: PropTypes.string,
    originalShiftType: PropTypes.string,
    lengthInWeeks: PropTypes.number,
    totalLengthInWeeks: PropTypes.number,
    staffingRequest: PropTypes.shape({
      id: PropTypes.string.isRequired,
      length: PropTypes.number,
      role: PropTypes.shape({
        abbreviation: PropTypes.string.isRequired,
      }),
      organization: PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
      }),
      organizationUnit: PropTypes.shape({
        department: PropTypes.shape({
          id: PropTypes.string.isRequired,
          organizationBusiness: PropTypes.shape({
            name: PropTypes.string.isRequired,
            city: PropTypes.string.isRequired,
            stateCode: PropTypes.string.isRequired,
          }),
        }),
        clinicalUnit: PropTypes.shape({
          id: PropTypes.string.isRequired,
          displayName: PropTypes.string.isRequired,
        }),
      }),
    }),
  }),
  children: PropTypes.any,
};

StrongColumnLabel.propTypes = {
  children: PropTypes.node,
};
